'use client';
import React, { useEffect, useState } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';
import { Heart, MapPin } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

export default function LandingFooter() {
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const supabase = createClient();
    supabase?.auth?.getUser()?.then(({ data: { user } }) => {
      setIsSignedIn(!!user);
    });
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((_event, session) => {
      setIsSignedIn(!!session?.user);
    });
    return () => subscription?.unsubscribe();
  }, []);

  return (
    <footer className="bg-surface border-t border-border pt-8 pb-16">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 2xl:px-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-2.5">
              <AppLogo size={40} />
              <span className="font-semibold text-base text-foreground whitespace-nowrap">Jamuhuri Singles Network</span>
            </div>
            <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
              A values-based matchmaking and singles network for Kenyan diaspora members across the United States. Connecting culture, community, and meaningful relationships.
            </p>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <MapPin size={12} />
              Serving Kenyan singles across all 50 US states
            </div>
          </div>

          {/* Platform Links */}
          <div className="space-y-4">
            <h4 className="font-semibold text-foreground text-sm">Platform</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Discover Profiles', href: '/discover-page' },
                { label: 'Matches', href: '/matches' },
                { label: 'Messages', href: '/messages' },
                { label: 'Membership Plans', href: '/membership' },
                { label: 'Sign In', href: '/sign-up-login-screen' },
              ]?.map((link) => (
                <li key={`footer-platform-${link?.label}`}>
                  <Link href={link?.href} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-foreground text-sm">Support</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Help & FAQ', href: '/help' },
                { label: 'Community Guidelines', href: '/safety' },
                { label: 'Safety Tips', href: '/safety' },
                { label: 'Privacy Policy', href: '/safety' },
                { label: 'Terms of Service', href: '/safety' },
              ]?.map((item) => (
                <li key={`footer-support-${item?.label}`}>
                  <Link href={item?.href} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    {item?.label}
                  </Link>
                </li>
              ))}
              {mounted && isSignedIn && (
                <li key="footer-support-report-issue">
                  <Link href="/report-issue" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    Report an Issue
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="divider-red mb-6" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col items-center md:items-start text-center md:text-left gap-0.5">
            <p className="text-xs text-muted-foreground">
              © 2026 Jamuhuri Singles Network
            </p>
            <p className="text-xs text-muted-foreground">
              All rights reserved
            </p>
            <p className="text-center md:text-left" style={{ fontSize: '11px', color: 'var(--muted-foreground)', opacity: 0.75 }}>
              A service of Relyon LLC
            </p>
          </div>
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
            Built with <Heart size={11} className="fill-primary text-primary" /> for the Kenyan diaspora
          </p>
        </div>
      </div>
    </footer>
  );
}